import { type } from "@/api/arktype";

import { funnelStage, temperature } from "@/api/triage/state-schema";
import { isoDate } from "@/lib/iso-date";

const TRIAGE_TRIGGERS = [
	"manual_run",
	"messages_synced",
	"action_contested",
	"action_completed",
	"action_changed",
	"appointment_changed",
	"financial_changed",
	"clinical_changed",
	"daily_sweep",
] as const;

const text = type("string > 0");
const nullableText = type([text, "|", "null"]);
const calendarDate = type(/^\d{4}-\d{2}-\d{2}$/u);
const nullableCalendarDate = type([calendarDate, "|", "null"]);

export const triageTrigger = type.enumerated(...TRIAGE_TRIGGERS);
export const triageRole = type.enumerated("medica", "secretaria");
export const triagePriority = type.enumerated("alta", "normal");
export const triageActor = type.enumerated("medica", "secretaria", "agente");
export const triageActionStatus = type.enumerated(
	"aberta",
	"aguardando",
	"feita",
	"contestada",
	"substituida",
);
export const triageWaitingFor = type.enumerated(
	"medica",
	"secretaria",
	"patient",
	"external",
	"date",
);
export const triageEventType = type.enumerated(
	"created",
	"updated",
	"contact",
	"waiting",
	"transferred",
	"completed",
	"contested",
	"closed",
	"reopened",
);
export const triageActionSurface = type.enumerated(
	"conversation",
	"person",
	"people",
	"agenda",
	"finance",
	"clinical",
);

const messageSource = type({
	"+": "delete",
	kind: type.enumerated("message"),
	id: text,
});
const appointmentSource = type({
	"+": "delete",
	kind: type.enumerated("appointment"),
	id: text,
});
const financialReceiptSource = type({
	"+": "delete",
	kind: type.enumerated("financial_receipt"),
	id: "number.integer > 0",
});
const clinicalMilestoneSource = type({
	"+": "delete",
	kind: type.enumerated("clinical_milestone"),
	id: "number.integer > 0",
});
const clinicalSectionSource = type({
	"+": "delete",
	kind: type.enumerated("clinical_section"),
	id: "number.integer > 0",
});
const clinicalManualSource = type({
	"+": "delete",
	kind: type.enumerated("clinical_manual"),
	id: "number.integer > 0",
});
const clinicalExamSource = type({
	"+": "delete",
	kind: type.enumerated("clinical_exam"),
	id: "number.integer > 0",
});
const personSource = type({
	"+": "delete",
	kind: type.enumerated("person"),
});
const actionEventSource = type({
	"+": "delete",
	kind: type.enumerated("action_event"),
	id: "number.integer > 0",
});

export const triageSource = messageSource
	.or(appointmentSource)
	.or(financialReceiptSource)
	.or(clinicalMilestoneSource)
	.or(clinicalSectionSource)
	.or(clinicalManualSource)
	.or(clinicalExamSource)
	.or(actionEventSource)
	.or(personSource);

const triageFactKind = type.enumerated("interest", "promise", "objection", "milestone", "referral");

const fact = type({
	"+": "delete",
	kind: triageFactKind,
	text,
	date: nullableCalendarDate,
	sources: triageSource.array().atLeastLength(1),
});

export const triageState = type({
	"+": "delete",
	funnel_stage: funnelStage,
	temperatura: temperature,
	summary: text,
	profile: text,
	communication: text,
	facts: fact.array(),
	review_on: nullableCalendarDate,
});

const actionFields = type({
	id: "number.integer > 0 | null",
	subject: text,
	role: triageRole,
	task: text,
	context: text,
	priority: triagePriority,
	sources: triageSource.array().atLeastLength(1),
	due_at: nullableText,
	next_review_at: text,
	reason: text,
});

const readyAction = type({
	status: type.enumerated("aberta"),
	waiting_for: "null",
	waiting_reason: "null",
}).and(
	type({ surface: type.enumerated("conversation"), draft: text }).or(
		type({ surface: triageActionSurface.exclude(type.enumerated("conversation")), draft: "null" }),
	),
);

const waitingAction = type({
	status: type.enumerated("aguardando"),
	surface: triageActionSurface,
	draft: "null",
	waiting_for: triageWaitingFor,
	waiting_reason: text,
});

const triageOpenAction = actionFields.and(readyAction.or(waitingAction)).onUndeclaredKey("delete");

const closedAction = type({
	"+": "delete",
	id: "number.integer > 0",
	status: type.enumerated("feita", "substituida"),
	reason: text,
	sources: triageSource.array().atLeastLength(1),
});

export const TRIAGE_SUBMIT_SCHEMA = type({
	"+": "delete",
	state: triageState.or("null"),
	changes: triageOpenAction.or(closedAction).array(),
});

export type TriageSubmitInput = typeof TRIAGE_SUBMIT_SCHEMA.infer;
export const triageSnapshot = type({ "[string]": "unknown" });

export interface TriagePayload extends TriageSubmitInput {
	covered_through: string | null;
	input_version: number;
}

export type TriageAction = typeof triageOpenAction.infer;
export type TriageSource = typeof triageSource.infer;

export function assertTriageDates(input: TriageSubmitInput) {
	if (input.state !== null && input.state.review_on !== null) {
		isoDate.assert(input.state.review_on);
	}

	for (const fact of input.state?.facts ?? []) {
		if (fact.date !== null) {
			isoDate.assert(fact.date);
		}
	}

	for (const change of input.changes) {
		if (!("subject" in change)) {
			continue;
		}

		for (const value of [change.due_at, change.next_review_at]) {
			if (
				value !== null &&
				(!/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/u.test(value) ||
					!Number.isFinite(Date.parse(value)))
			) {
				throw new Error("triage_invalid_instant");
			}
		}
	}
}
